import { Object3D, Vector2Tuple, Vector3Tuple } from 'three';

import { GraphNode } from '@spazious/dollhouse-graph';
import {
  AnnotationShallowNode,
  CameraPathShallowNode,
  DecorationShallowNode,
  FAreaShallowNode,
  FCompositionShallowNode,
  FEnsembleTypesEnum,
  FPolygonShallowNode,
  FShallowBase,
  FShallowGraph,
  FurnitureShallowNode,
  MeasurementShallowNode,
} from '@spazious/dollhouse-shallow-graph';
import { Outline2D } from '@spazious/dollhouse-three';

import { Collider, NodeLayers } from '../core/index.js';
import { FArrangement } from '../farrangements/index.js';
import { FEnsemble } from '../fensembles/index.js';

export interface FNodeRoot extends GraphNode<FNodeRoot, FNodeParent, FNodeChild> {
  layers: NodeLayers;
  shallow: FShallowGraph;
}

export type FNodeParent = FNodeRoot | FNode;
export type FNodeChild = FNode;

export enum FNodeTypesEnum {
  Furniture = 'furniture',
  Decoration = 'decoration',
  FComposition = 'fcomposition',
  FGroup = 'fgroup',
  FPolygon = 'fpolygon',
  FArea = 'farea',
  Measurement = 'measurement',
  Annotation = 'annotation',
  CameraPath = 'camerapath',
}
declare module '@spazious/dollhouse-graph' {
  export interface GraphNodeEvents {
    update: [];
    added: [];
    removed: [];
    checkCollisions: [];
    flushCollisions: [];
  }
}

export interface FNodeBase extends GraphNode<FNodeRoot, FNodeParent, FNodeChild> {
  shallow: FShallowBase | null;
  selector: string;
  shallowParent?: string | null;
  position: Vector2Tuple;
  positionY?: number;
  rotation: number;
  // TODO meta will only be available in the shallow node
  meta?: Record<string, any>;
  findBySelector(selector: string): FNode | null;
}

export interface FurnitureNode extends FNodeBase {
  id: number;
  name: string;
  type: FNodeTypesEnum.Furniture;
  url: string;
  furnitureType: string;
  visible: boolean;
  object: Object3D | null;
  collider: Collider | null;
  collisions: ColliderNode[];
  /**
   * The originator of the furniture node.
   */
  originator: FCompositionNode | null;
  shallow: FurnitureShallowNode | null;
  outline2D: Outline2D | null;
  computeOutline2D(): void;
}

export interface DecorationNode extends FNodeBase {
  id: number;
  name: string;
  type: FNodeTypesEnum.Decoration;
  url: string;
  decorationType: string;
  visible: boolean;
  collider: Collider | null;
  object: Object3D | null;
  collisions: ColliderNode[];
  shallow: DecorationShallowNode | null;
  outline2D: Outline2D | null;
  computeOutline2D(): void;
}
export interface FCompositionNode<TInstance = FEnsemble | FArrangement> extends FNodeBase {
  id: number;
  type: FNodeTypesEnum.FComposition;
  visible: boolean;
  instance: TInstance;
  shallow: FCompositionShallowNode | null;
  outline2D: Outline2D | null;
  computeOutline2D(): void;
}

export interface FGroupNode extends FNodeBase {
  type: FNodeTypesEnum.FGroup;
}

export interface FPolygonNode extends FNodeBase {
  id: number;
  type: FNodeTypesEnum.FPolygon;
  visible: boolean;
  points: Vector2Tuple[];
  shallow: FPolygonShallowNode | null;
}

export interface FAreaNode extends FNodeBase {
  type: FNodeTypesEnum.FArea;
  visible: boolean;
  points: Vector2Tuple[];
  shallow: FAreaShallowNode | null;
}

export interface MeasurementNode extends FNodeBase {
  type: FNodeTypesEnum.Measurement;
  visible: boolean;
  points: Vector2Tuple[];
  shallow: MeasurementShallowNode | null;
}

export interface AnnotationNode extends FNodeBase {
  type: FNodeTypesEnum.Annotation;
  visible: boolean;
  shallow: AnnotationShallowNode | null;
  text: string;
}

export interface CameraPathNode extends FNodeBase {
  type: FNodeTypesEnum.CameraPath;
  visible: boolean;
  points: [Vector3Tuple, Vector3Tuple][];
  shallow: CameraPathShallowNode | null;
}

export type FNode =
  | FurnitureNode
  | FCompositionNode
  | FGroupNode
  | FPolygonNode
  | FAreaNode
  | MeasurementNode
  | AnnotationNode
  | DecorationNode
  | CameraPathNode;

export type ColliderNode = FurnitureNode | DecorationNode;

/**
 * Interface for the algorithms node.
 */
export interface FInstanceNode {
  type: 'table' | 'chair';
  id: number;
  position: Vector2Tuple;
  rotation: number;
  children?: FInstanceNode[];
}

export interface FInstanceEnsembleNode {
  type: FEnsembleTypesEnum;
  position: Vector2Tuple;
  rotation: number;
  attrs: Record<string, any>;
}
